// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/work-optimization/BatchOpportunityCard.jsx

import React from "react";

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function titleCase(value) {
  const text = clean(value);
  if (!text) return "";
  return text
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\bIam\b/g, "IAM")
    .replace(/\bAws\b/g, "AWS")
    .replace(/\bS3\b/g, "S3")
    .replace(/\bMfa\b/g, "MFA");
}

function formatMinutes(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "—";
  if (parsed < 60) return `${Math.round(parsed)} min`;
  const hours = parsed / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hr`;
}

function normalizeTaskId(value) {
  const text = clean(value);
  if (!text) return "";
  return text.startsWith("task:") ? text.slice(5) : text;
}

function activeTaskId(activeTask = {}) {
  const candidates = [
    activeTask?.work_item_id,
    activeTask?.task_id,
    activeTask?.taskId,
    activeTask?.remediation_task_id,
    activeTask?.remediationTaskId,
    activeTask?.id,
    activeTask?.raw_task?.id,
  ]
    .map(normalizeTaskId)
    .filter(Boolean);

  return candidates[0] || "";
}

function activeTaskRule(activeTask = {}) {
  return clean(activeTask?.rule_id || activeTask?.ruleId || activeTask?.raw_task?.rule_id).toUpperCase();
}

function reasonList(batch = {}) {
  const rows = Array.isArray(batch.why)
    ? batch.why
    : Array.isArray(batch.reasons)
      ? batch.reasons
      : Array.isArray(batch.operator_reasons)
        ? batch.operator_reasons
        : [];

  return rows.map(clean).filter(Boolean);
}

function arrayFrom(value) {
  return Array.isArray(value) ? value : [];
}

function groupLabel(group = {}) {
  return clean(group.label || group.name || group.category || group.rule_family || group.rule_id) || "Related group";
}

function groupCount(group = {}) {
  const count = Number(group.count || group.task_count || arrayFrom(group.task_ids).length);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

function normalizeChildGroups(batch = {}) {
  const childGroups = arrayFrom(batch.child_groups);
  const ruleGroups = arrayFrom(batch.rule_groups);
  const sourceGroups = arrayFrom(batch.groups);

  const rows = childGroups.length ? childGroups : ruleGroups.length ? ruleGroups : sourceGroups;

  return rows
    .map((group) => ({
      ...group,
      label: groupLabel(group),
      count: groupCount(group),
      task_ids: arrayFrom(group.task_ids).map(normalizeTaskId).filter(Boolean),
    }))
    .filter((group) => group.label || group.count);
}

function suggestedOrderFromGroups(groups = []) {
  const preferred = [
    "mfa",
    "2sv",
    "access key",
    "credential",
    "secret",
    "dependabot",
    "branch",
    "waf",
    "dnssec",
    "https",
    "tls",
    "unused",
    "trust",
    "admin",
    "wildcard",
    "logging",
    "versioning",
    "encryption",
    "public",
  ];

  const withRank = groups.map((group, index) => {
    const label = group.label.toLowerCase();
    const rank = preferred.findIndex((token) => label.includes(token));
    return {
      ...group,
      rank: rank === -1 ? 999 + index : rank,
      originalIndex: index,
    };
  });

  return withRank.sort((left, right) => left.rank - right.rank || left.originalIndex - right.originalIndex);
}

function selectedGroup(groups = [], selectedTaskId = "", selectedRule = "") {
  if (!groups.length) return null;

  if (selectedTaskId) {
    const direct = groups.find((group) => arrayFrom(group.task_ids).map(normalizeTaskId).includes(selectedTaskId));
    if (direct) return direct;
  }

  if (selectedRule) {
    const ruleToken = selectedRule.toLowerCase().replace(/[_-]+/g, " ");
    const direct = groups.find((group) => {
      const label = clean(group.label).toLowerCase();
      const ruleId = clean(group.rule_id || group.ruleId).toLowerCase().replace(/[_-]+/g, " ");
      return label.includes(ruleToken) || ruleToken.includes(label) || ruleId === ruleToken;
    });
    if (direct) return direct;
  }

  return groups[0] || null;
}

function batchCount(batch = {}, taskIds = [], groups = []) {
  const count = Number(
    batch.count ||
      batch.task_count ||
      taskIds.length ||
      groups.reduce((total, group) => total + Number(group.count || 0), 0),
  );

  return Number.isFinite(count) && count > 0 ? count : 0;
}

function batchSummary(batch = {}, count = 0) {
  const explicit = clean(batch.summary || batch.description || batch.operator_summary);
  if (explicit) return explicit;

  const label = clean(batch.label || batch.name);
  if (label.toLowerCase().includes("iam")) {
    return "Handle related IAM hygiene items together so access reviews, evidence refresh, and validation can be completed in one operator pass.";
  }

  if (label.toLowerCase().includes("s3")) {
    return "Handle related S3 posture items together so bucket configuration, evidence refresh, and validation stay aligned.";
  }

  if (label.toLowerCase().includes("github")) {
    return "Handle related repository security settings together so branch, dependency, and secret-scanning controls stay aligned.";
  }

  if (label.toLowerCase().includes("cloudflare")) {
    return "Handle related Cloudflare zone controls together so edge security settings and evidence refresh stay aligned.";
  }

  return `${count || "Multiple"} related findings can be reviewed and remediated as an operator-planned batch.`;
}

function batchGuardrailText() {
  return "Batching is advisory only. OASYS does not merge tasks, change owners, close work, validate fixes, or permanently reorder priorities.";
}

export default function BatchOpportunityCard({ batch, activeTask, onOpenTask }) {
  const safeBatch = batch && typeof batch === "object" ? batch : null;
  if (!safeBatch) return null;

  const taskIds = Array.isArray(safeBatch.task_ids)
    ? safeBatch.task_ids.map(normalizeTaskId).filter(Boolean)
    : [];

  const childGroups = normalizeChildGroups(safeBatch);
  const orderedGroups = suggestedOrderFromGroups(childGroups);
  const reasons = reasonList(safeBatch);
  const selectedTaskId = activeTaskId(activeTask);
  const selectedRule = activeTaskRule(activeTask);
  const currentGroup = selectedGroup(childGroups, selectedTaskId, selectedRule);
  const openTarget = selectedTaskId && taskIds.includes(selectedTaskId) ? selectedTaskId : taskIds[0];
  const currentTaskIsOpenTarget = Boolean(openTarget && selectedTaskId && openTarget === selectedTaskId);
  const count = batchCount(safeBatch, taskIds, childGroups);
  const savings = safeBatch.estimated_time_saved_minutes || safeBatch.time_saved_minutes;
  const label = safeBatch.label || safeBatch.name || "Related work";

  return (
    <section className="wo-card wo-batch-card" aria-label="Batch opportunity">
      <div className="wo-card-header">
        <div>
          <span>Batch Opportunity</span>
          <h3>{label}</h3>
          <p>{batchSummary(safeBatch, count)}</p>
        </div>
        <div className="wo-card-stat">
          <strong>{formatMinutes(savings)}</strong>
          <small>est. saved</small>
        </div>
      </div>

      <div className="wo-batch-metrics" aria-label="Batch opportunity metrics">
        <div>
          <span>Findings</span>
          <strong>{count || "—"}</strong>
        </div>
        <div>
          <span>Groups</span>
          <strong>{childGroups.length || "—"}</strong>
        </div>
        <div>
          <span>Linked Tasks</span>
          <strong>{taskIds.length || "—"}</strong>
        </div>
      </div>

      {currentGroup ? (
        <div className="wo-batch-current">
          <span>This task is part of</span>
          <strong>{titleCase(currentGroup.label)}</strong>
          <small>
            This selected task is already open in Active Work. Use Review / Manage below to open the focused task modal.
          </small>
        </div>
      ) : null}

      {orderedGroups.length ? (
        <div className="wo-batch-order">
          <span>Suggested Order</span>
          <ol>
            {orderedGroups.slice(0, 6).map((group, index) => (
              <li key={`batch-order-${group.label}-${index}`}>
                <strong>{titleCase(group.label)}</strong>
                <small>{group.count || "Multiple"} task{group.count === 1 ? "" : "s"}</small>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {childGroups.length ? (
        <div className="wo-child-groups">
          {childGroups.slice(0, 6).map((group, index) => (
            <div key={`${group.label || "group"}-${group.count || index}`}>
              <strong>{titleCase(group.label)}</strong>
              <small>{Number(group.count || 0)} task{Number(group.count || 0) === 1 ? "" : "s"}</small>
            </div>
          ))}
        </div>
      ) : null}

      {reasons.length ? (
        <div className="wo-batch-why">
          <span>Why this is a batch</span>
          <ul className="wo-reasons">
            {reasons.slice(0, 3).map((reason, index) => <li key={`batch-reason-${index}`}>{reason}</li>)}
          </ul>
        </div>
      ) : null}

      <div className="wo-batch-guardrail">
        <strong>Guardrail</strong>
        <small>{batchGuardrailText()}</small>
      </div>

      {taskIds.length ? (
        <div className="wo-batch-footer">
          <small>{taskIds.length} linked task{taskIds.length === 1 ? "" : "s"}</small>
          <button
            type="button"
            className="mec-link-button"
            onClick={() => {
              if (!currentTaskIsOpenTarget) onOpenTask?.(openTarget);
            }}
            disabled={currentTaskIsOpenTarget}
            title={
              currentTaskIsOpenTarget
                ? "This batch task is already selected in Active Work."
                : "Open the first related task in Active Work."
            }
          >
            {currentTaskIsOpenTarget ? "Current Batch Task Selected" : "Open First Related Task"}
          </button>
        </div>
      ) : null}
    </section>
  );
}
